import { frameAtTick, PF, type Replay } from "@69ly/shared";

/**
 * Each team's money round by round: what it started with, spent and carried, and the
 * kind of buy that makes (pistol, eco, force, full). From the money and buys read with
 * the demo (replays of 0.15.0 and later); older replays give the start money only.
 */

export type BuyType = "pistol" | "eco" | "force" | "full";
export const BUY_TYPES: readonly BuyType[] = ["pistol", "eco", "force", "full"];

/** Equipment per player, on average, below which a buy is an eco, then a force. */
const ECO_BELOW = 1500;
const FORCE_BELOW = 3500;

export interface TeamRound {
  team: 0 | 1;
  side: "t" | "ct";
  /** Money of the team at the start of the round. */
  start: number;
  spent: number;
  /** Equipment carried once the buy time was over; null when the replay does not say. */
  value: number | null;
  buy: BuyType | null;
  won: boolean;
  players: { player: number; start: number; spent: number; value: number | null; buys: string[] }[];
}

export interface EconomyRound {
  /** Index into `rounds`. */
  round: number;
  number: number;
  teams: [TeamRound, TeamRound];
}

/** Whether the replay carries buys (format 3 and later). */
export const hasBuys = (r: Replay) => !!r.economy?.length;

/** The first round of each half (and only those) is a pistol round: overtime starts with money. */
function pistolRounds(r: Replay): Set<number> {
  const out = new Set<number>([0]);
  const swap = r.rounds.findIndex((x, i) => i > 0 && x.tTeam !== r.rounds[i - 1].tTeam);
  if (swap > 0 && swap <= 15) out.add(swap);
  return out;
}

export function buyType(valuePerPlayer: number): Exclude<BuyType, "pistol"> {
  return valuePerPlayer < ECO_BELOW ? "eco" : valuePerPlayer < FORCE_BELOW ? "force" : "full";
}

export function economyOf(r: Replay): EconomyRound[] {
  const pistols = pistolRounds(r);
  const P = r.players.length;
  return r.rounds.map((round, index) => {
    const entries = (r.economy ?? []).filter((e) => e.round === index);
    const teams = ([0, 1] as const).map((team): TeamRound => {
      const members = r.players.map((p, i) => ({ p, i })).filter((x) => x.p.team === team);
      const players = members.map(({ i }) => {
        const e = entries.find((x) => x.player === i);
        if (e) return { player: i, start: e.start, spent: e.spent, value: e.value, buys: e.buys.map((w) => r.weapons[w] ?? "") };
        // Older replays: the money at the end of freeze time, as spent as it gets.
        const f = frameAtTick(r.columns.frameTicks, round.frameFrom, round.frameTo, round.startTick);
        const at = f * P + i;
        const present = (r.columns.flags[at] & PF.present) !== 0;
        return { player: i, start: present ? r.columns.money[at] : 0, spent: 0, value: null, buys: [] };
      });
      const known = players.filter((x) => x.value !== null);
      const value = known.length ? known.reduce((n, x) => n + (x.value ?? 0), 0) : null;
      const buy = pistols.has(index) ? "pistol" : value === null ? null : buyType(value / Math.max(1, known.length));
      const side = team === round.tTeam ? "t" : "ct";
      return {
        team,
        side,
        start: players.reduce((n, x) => n + x.start, 0),
        spent: players.reduce((n, x) => n + x.spent, 0),
        value,
        buy,
        won: round.winner === side,
        players,
      };
    }) as [TeamRound, TeamRound];
    return { round: index, number: round.number, teams };
  });
}

export interface BuyMatchup {
  ours: BuyType;
  theirs: BuyType;
  played: number;
  won: number;
}

/** Rounds won by one team for each pair of buys: its buy against the other's. */
export function buyMatchups(rounds: EconomyRound[], team: 0 | 1): BuyMatchup[] {
  const out = new Map<string, BuyMatchup>();
  for (const r of rounds) {
    const ours = r.teams[team];
    const theirs = r.teams[1 - team];
    if (!ours.buy || !theirs.buy) continue;
    const key = `${ours.buy}:${theirs.buy}`;
    const m = out.get(key) ?? out.set(key, { ours: ours.buy, theirs: theirs.buy, played: 0, won: 0 }).get(key)!;
    m.played++;
    if (ours.won) m.won++;
  }
  return BUY_TYPES.flatMap((a) => BUY_TYPES.map((b) => out.get(`${a}:${b}`))).filter((m): m is BuyMatchup => !!m);
}
